import { db } from '@/mocks/db';

import { mapAttendanceRecordToDailyRow } from '../../-api/mapAttendanceRecordToDailyRow';
import { normalizeAttendanceRecordForDemo } from '../../-api/normalizeAttendanceRecordForDemo';
import {
  CLOCKING_RECORDS_DEMO_ORG_ID,
  CLOCKING_RECORDS_FALLBACK_YEAR_MONTH,
} from '../../-constants';
import type { ClockingRecordsDailyRow } from '../../-types';

import {
  computeEmployeeAttendanceBookSummary,
  type EmployeeAttendanceBookSummary,
} from './computeAttendanceBookSummary';

export type { EmployeeAttendanceBookSummary };

export type EmployeeClockingProfile = {
  employeeId: string;
  employeeName: string;
  department: string;
  workStyleName: string;
  avatarUrl: string | null;
};

function monthPrefix(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * 打刻または日次勤怠から、当該従業員の最新年月を推定する。
 */
export async function getEmployeeLatestClockingPeriodFromDb(
  employeeId: string,
): Promise<{ year: number; month: number } | null> {
  const records = await db.clockingRecords
    .where('employeeId')
    .equals(employeeId)
    .toArray();

  let best: { y: number; m: number } | null = null;
  for (const r of records) {
    const d = new Date(r.timestamp);
    if (Number.isNaN(d.getTime())) continue;
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    if (!best || y > best.y || (y === best.y && m > best.m)) {
      best = { y, m };
    }
  }

  if (best) {
    return { year: best.y, month: best.m };
  }

  const daily = await db.attendanceRecords
    .filter(
      (r) =>
        r.employeeId === employeeId &&
        r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID,
    )
    .toArray();

  for (const r of daily) {
    if (r.date.length < 7) continue;
    const y = Number(r.date.slice(0, 4));
    const m = Number(r.date.slice(5, 7));
    if (!Number.isFinite(y) || !Number.isFinite(m)) continue;
    if (!best || y > best.y || (y === best.y && m > best.m)) {
      best = { y, m };
    }
  }

  if (best) {
    return { year: best.y, month: best.m };
  }

  const [fy, fm] = CLOCKING_RECORDS_FALLBACK_YEAR_MONTH.split('-').map(Number);
  if (Number.isFinite(fy) && Number.isFinite(fm)) {
    return { year: fy, month: fm };
  }
  return null;
}

export async function getEmployeeClockingProfile(
  employeeId: string,
): Promise<EmployeeClockingProfile | null> {
  const row = await db.attendanceEmployees
    .where('employeeId')
    .equals(employeeId)
    .first();
  if (!row) return null;
  const legacy = row as typeof row & { workStyleName?: string };
  return {
    employeeId: row.employeeId,
    employeeName: row.employeeName,
    department: row.department,
    workStyleName: row.attendanceStructureName ?? legacy.workStyleName ?? '',
    avatarUrl: null,
  };
}

export type EmployeeAttendanceBookBundle = {
  rows: ClockingRecordsDailyRow[];
  summary: EmployeeAttendanceBookSummary;
};

/**
 * 出勤簿タブ用: 指定従業員・年月の日次行と月次サマリ（Dexie デモ）
 */
export async function getEmployeeAttendanceBookBundle(
  employeeId: string,
  year: number,
  month: number,
): Promise<EmployeeAttendanceBookBundle> {
  const prefix = monthPrefix(year, month);
  const raw = await db.attendanceRecords
    .filter(
      (r) =>
        r.employeeId === employeeId &&
        r.organizationId === CLOCKING_RECORDS_DEMO_ORG_ID &&
        r.date.startsWith(prefix),
    )
    .toArray();

  const normalized = raw.map(normalizeAttendanceRecordForDemo);
  const sorted = [...normalized].sort((a, b) => a.date.localeCompare(b.date));
  const summary = computeEmployeeAttendanceBookSummary(sorted, year, month);
  const rows = sorted.map(mapAttendanceRecordToDailyRow);

  return { rows, summary };
}
